import { ComponentType } from '@angular/cdk/portal';
import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '@uirouter/core';
import { controls } from '@caslink/device-client/ng-app/shared/models/device-routes.enum';
import { ControlService } from '../control/control.service';
import { DeviceModelService } from '../shared/services/device-model.service';
import { DeviceUpdateAction, IControlModel } from '../shared/models/models';
import { IDeviceInformation, IDeviceNode } from '@caslink/device-client/ng-app/shared/models/device';
import { DashboardToastrConfirmationService } from '../shared/services/dashboard-toastr-confirmation.service';

@Component({
    template: '',
})
export class DeviceSettingsBaseComponent {
    @Input() public currentControl: IControlModel;
    @Input() public hardwareItemId: number;
    @Input() public locationId: number;
    @Input() public areaId: number;

    public isLoaded: boolean = false;
    public modelName: string;

    // The loaded device information: -- this one will be edited as the form is edited
    private _deviceInformation: IDeviceInformation;
    public set deviceInformation(value: IDeviceInformation) {
        this._deviceInformation = value;
    }
    public get deviceInformation(): IDeviceInformation {
        return this._deviceInformation;
    }

    // The loaded device information -- always keep in initial state
    private _originalDeviceInformation: IDeviceInformation;
    public set originalDeviceInformation(value: IDeviceInformation) {
        this._originalDeviceInformation = value;
    }

    constructor(
        protected $state: StateService,
        protected deviceModelService: DeviceModelService,
        protected controlService: ControlService,
        protected dialog: MatDialog,
        protected dashboardToastrConfirmationService: DashboardToastrConfirmationService,
    ) {
    }

    protected cancelChanges(cancelRoute: string = controls.controlsDetails) {
        this.$state.go(cancelRoute, {}, { location: !!cancelRoute });
    }

    protected updateDevice(confirmationCallback: () => void, action: DeviceUpdateAction, note: string) {
        this.updateCardStatus('Submitting');

        // Schedule time slots must be sorted!
        this.sortScheduleTimeSlotByStartTime(this.deviceInformation.$deviceHierarchy);

        /* The models are too long for SignalR with all of those visitor-added properties, so we need to strip them */
        let originalViewModel = DeviceSettingsBaseComponent.stripVisitorProperties(this._originalDeviceInformation.$deviceHierarchy);
        let changedViewModel = DeviceSettingsBaseComponent.stripVisitorProperties(this._deviceInformation.$deviceHierarchy);

        this.currentControl.confirmationSubscription =
            this.deviceModelService.startViewModelConfirmationSubscription(
                this.locationId,
                this.areaId,
                this.currentControl.confirmationSubscription, {
                    originalViewModel: originalViewModel,
                    changedViewModel: changedViewModel,
        });
        this.updateDeviceToastr(confirmationCallback, action, note);
    }

    private updateDeviceToastr(confirmationCallback: () => void, action: DeviceUpdateAction, note: string) {
        const data = {
            hardwareItemId: this.hardwareItemId,
            locationId: this.locationId,
            deviceInfo: this._deviceInformation,
            note: note,
            name: this.currentControl.text,
            action,
        };

        this.dashboardToastrConfirmationService.update(data).then((backendResultSubject) => {
            backendResultSubject.subscribe({
                complete: () => {
                    this.endUpdate(confirmationCallback);
                }}
            );
        });
    }

    private endUpdate(confirmationCallback: () => void) {
        this.updateCardStatus('Received');
        this.stopConfirmationSubscription();
        confirmationCallback.call(this);
    }

    private stopConfirmationSubscription() {
        this.deviceModelService.stopViewModelConfirmationSubscription(
            this.locationId,
            this.areaId,
            this.currentControl.confirmationSubscription);
    }

    private updateCardStatus(cardStatus: 'Submitting' | 'Received') {
        this.controlService.broadcastCardStatusUpdate({
            locationId: this.locationId,
            hardwareItemId: this.hardwareItemId,
            status: cardStatus,
        });
    }

    private sortScheduleTimeSlotByStartTime(deviceInformation: IDeviceNode) {
        let days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        days.forEach(day => {
            if (DeviceSettingsBaseComponent.isDeviceInformationExistForCertainDay(deviceInformation, day)) {
                deviceInformation['schedule'][day].items.sort(
                    (a, b) => {
                        return (a.value.startTime.value > b.value.startTime.value) ? 1 :
                            ((b.value.startTime.value > a.value.startTime.value) ? -1 : 0);
                    });
            }
        });
    }

    private static isDeviceInformationExistForCertainDay(deviceInformation: IDeviceNode, day: string) {
        return deviceInformation &&
            deviceInformation['schedule'] &&
            deviceInformation['schedule'][day] &&
            deviceInformation['schedule'][day].items &&
            deviceInformation['schedule'][day].items.length > 0;
    }

    public static stripVisitorProperties(node: IDeviceNode): any {
        return JSON.parse(JSON.stringify(node, DeviceSettingsBaseComponent.serializeSettings));
    }

    private static serializeSettings(key, value) {
        let val = value;

        if (typeof key === 'string' && key.charAt(0) === '$') {
            val = undefined;
        }

        return val;
    }
}
